// @givmo/board — the uploads' tolerant decode and the small decisions the upload page makes
// (CBG-AI5-INBOX-WEB, D-CBG-41; backend `agent_docs/contracts/board-ai-proposals.md`, the import
// inbox's routes).
//
// The page reads the generated `BoardImportInboxOut` tolerantly: a malformed row is dropped, never
// thrown on, and a missing count or limit falls back to what the rows and the design say. Every file
// name is the person's own, rendered as plain text only.

import type { BoardImportInboxFileOut, BoardImportInboxOut } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { MAX_FILE_BYTES } from '../proposals/proposal-decode';
import { fileKindWord } from '../proposals/proposal-words';
import { uploadWords as w } from './upload-words';

export type InboxFile = BoardImportInboxFileOut;

/** The page's read, decoded: three lists (each sorted by name, as served), counts and limits. */
export type ImportInbox = {
  waiting: InboxFile[];
  proposed: InboxFile[];
  imported: InboxFile[];
  waitingCount: number;
  proposedCount: number;
  importedCount: number;
  heldByteSize: number;
  maxFiles: number;
  maxByteSize: number;
  maxFileByteSize: number;
};

/** The design's limits (§3), for an answer that leaves one out. */
const FILES_MAX = 1000;
const BYTES_MAX = 10 * 1024 * 1024 * 1024;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isText(value: unknown): value is string {
  return typeof value === 'string';
}

function count(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}

function files(value: unknown, state: InboxFile['state']): InboxFile[] {
  return extractList<InboxFile>(value).filter(
    (row) =>
      isObject(row) &&
      isText(row.id) &&
      row.state === state &&
      isText(row.file_name) &&
      isText(row.content_type) &&
      typeof row.byte_size === 'number',
  );
}

/** The page's read, or `null` for a body that is not the uploads' answer. */
export function decodeImportInbox(body: unknown): ImportInbox | null {
  if (!isObject(body)) return null;
  const answer = body as Partial<BoardImportInboxOut> & Record<string, unknown>;
  const waiting = files(answer.waiting, 'waiting');
  const proposed = files(answer.proposed, 'proposed');
  const imported = files(answer.imported, 'imported');
  return {
    waiting,
    proposed,
    imported,
    waitingCount: count(answer.waiting_count, waiting.length),
    proposedCount: count(answer.proposed_count, proposed.length),
    importedCount: count(answer.imported_count, imported.length),
    heldByteSize: count(
      answer.held_byte_size,
      [...waiting, ...proposed].reduce((sum, file) => sum + file.byte_size, 0),
    ),
    maxFiles: count(answer.max_files, FILES_MAX),
    maxByteSize: count(answer.max_byte_size, BYTES_MAX),
    maxFileByteSize: count(answer.max_file_byte_size, MAX_FILE_BYTES),
  };
}

/** Whether the uploads hold no file at all (frame u2: the drop zone takes the page). */
export function isEmpty(inbox: ImportInbox): boolean {
  return inbox.waitingCount + inbox.proposedCount + inbox.importedCount === 0;
}

/** The kind a file is, in the word a person knows it by; a kind Board takes none of is "Other". A
 *  Word file is one Board takes since CBG-FIX160 H, a .docm kept by Board's check as the .docx it
 *  rewrote. */
export function kindWord(contentType: string | null | undefined): string {
  switch (contentType) {
    case 'application/pdf':
    case 'image/jpeg':
    case 'image/png':
    case 'image/heic':
    case 'image/tiff':
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return fileKindWord(contentType);
    default:
      return w.otherKind;
  }
}

/** Whether a file is a picture (its row's icon). */
export function isPicture(contentType: string | null | undefined): boolean {
  return typeof contentType === 'string' && contentType.startsWith('image/');
}

/** The limits as the refusal writes them: "1,000" files, "10 GB". */
export function limitWords(inbox: ImportInbox): { files: string; bytes: string } {
  const gigabytes = inbox.maxByteSize / (1024 * 1024 * 1024);
  const bytes = Number.isInteger(gigabytes)
    ? `${gigabytes} GB`
    : `${Math.round(inbox.maxByteSize / (1024 * 1024))} MB`;
  return { files: inbox.maxFiles.toLocaleString('en-US'), bytes };
}

/**
 * Whether a folder's files fit beside the uploads already held (design §3): a folder over either
 * limit is refused whole before anything uploads. Only files Board would take count.
 */
export function fitsTheLimits(inbox: ImportInbox, sizes: readonly number[]): boolean {
  const held = inbox.waitingCount + inbox.proposedCount;
  const bytes = sizes.reduce((sum, size) => sum + size, 0);
  return held + sizes.length <= inbox.maxFiles && inbox.heldByteSize + bytes <= inbox.maxByteSize;
}

/** The proposed files by the set they wait in (each set is one confirmation page), in order served. */
export type ProposedSet = { batchId: string; files: InboxFile[] };

export function proposedSets(proposed: readonly InboxFile[]): ProposedSet[] {
  const sets = new Map<string, ProposedSet>();
  for (const file of proposed) {
    const batchId = file.batch_id ?? '';
    if (batchId.length === 0) continue;
    const set = sets.get(batchId) ?? { batchId, files: [] };
    set.files.push(file);
    sets.set(batchId, set);
  }
  return [...sets.values()];
}
